import React from 'react';
import ExpandableTextarea from '../../../components/ExpandableTextarea';
import { Field } from './_modal-helpers';
import {
  EVENT_TYPE_OPTS,
  FLIGHT_PART_GROUPS,
  cancelFee,
  fmt,
  flightPartTotal,
  formatAmountInput,
  hasValue,
  num,
  parseAmountInput,
  supplierEffect,
} from './RevenueEventModal.helpers';
import { MobilePaymentCard, MobileRefundCard } from './RevenueEventModal.mobileLedger';
import {
  mobileActions,
  mobileAmountInput,
  mobileCard,
  mobileCardHead,
  mobileCardList,
  mobileCardName,
  mobileCardSub,
  mobileCardTitle,
  mobileDangerBtn,
  mobileEffect,
  mobileEmpty,
  mobileFieldLabel,
  mobileFieldWrap,
  mobileHeaderGrid,
  mobileHero,
  mobileHeroAmount,
  mobileHeroLabel,
  mobileLabelInput,
  mobileMetric,
  mobileMetricGrid,
  mobileMetricLabel,
  mobileMetricValue,
  mobileMiniBtn,
  mobileNoteLabel,
  mobileNoteTextarea,
  mobileNoteWrap,
  mobilePartBox,
  mobilePartGrid,
  mobilePartTitle,
  mobilePartTotal,
  mobileReadonlyLabel,
  mobileReadonlyLine,
  mobileReadonlyValue,
  mobileSection,
  mobileSectionHead,
  mobileSectionSub,
  mobileSectionTitle,
  mobileShell,
} from './RevenueEventModal.mobileStyles';

const PART_LABELS = ['운임', '유류', '공항세'];

function hasFlightPartInput(item, group) {
  return FLIGHT_PART_GROUPS[group].some((key) => hasValue(item?.[key]));
}

function effectiveServiceItem(item) {
  if (item.item_type !== 'flight') return item;
  const hasRefundInput = hasFlightPartInput(item, 'refund');
  const hasAdditionalInput = hasFlightPartInput(item, 'additional');
  return {
    ...item,
    target_cost: flightPartTotal(item, 'target') || num(item.target_cost),
    supplier_refund: hasRefundInput ? flightPartTotal(item, 'refund') : null,
    additional_cost: hasAdditionalInput ? flightPartTotal(item, 'additional') : null,
  };
}

export default function MobileRevenueEventLayout({
  form,
  upd,
  reservationNotes,
  setReservationNotes,
  serviceRows,
  updateService,
  fixedFlightCount,
  fixedHotelCount,
  addFlightRow,
  removeFlightRow,
  addHotelRow,
  removeHotelRow,
  payments,
  addPayment,
  updatePayment,
  removePayment,
  refunds,
  addRefund,
  updateRefund,
  removeRefund,
  preview,
  customer,
  customerBalance,
  linkedPaymentTotal,
  linkedRefundTotal,
}) {
  return (
    <div style={mobileShell}>
      <div style={mobileHeaderGrid}>
        <Field label="수익 반영일" required type="date" value={form.event_date} onChange={upd('event_date')} />
        <Field label="유형" value={form.event_type} onChange={upd('event_type')} options={EVENT_TYPE_OPTS} />
        <div style={mobileNoteWrap}>
          <span style={mobileNoteLabel}>예약 메모</span>
          <ExpandableTextarea value={reservationNotes} onChange={setReservationNotes} rows={2} expandedRows={6} style={mobileNoteTextarea} />
        </div>
      </div>

      <div style={mobileHero(num(preview.amount) < 0)}>
        <span style={mobileHeroLabel}>이번 달 반영</span>
        <strong style={mobileHeroAmount(num(preview.amount) < 0)}>{fmt(preview.amount)}</strong>
      </div>

      <div style={mobileMetricGrid}>
        <Metric label="기존 고객결제" value={customer.target_cost} />
        <Metric label="고객 보유액" value={customerBalance} />
        <Metric label="추가결제" value={linkedPaymentTotal} />
        <Metric label="고객환불" value={linkedRefundTotal} danger={num(linkedRefundTotal) > 0} />
        <Metric label="공급사 환불" value={preview.serviceRefund} />
        <Metric label="추가 원가" value={preview.additionalCost} danger={num(preview.additionalCost) > 0} />
      </div>

      <MobileSection
        title="공급사 정산"
        sub="기준 원가를 확인하고 이번 변경/취소 금액만 입력합니다."
        actions={(
          <>
            <button type="button" style={mobileMiniBtn} onClick={addFlightRow}>+ 항공</button>
            <button type="button" style={mobileMiniBtn} onClick={addHotelRow}>+ 박 추가</button>
          </>
        )}
      >
        <div style={mobileCardList}>
          {serviceRows.flight.map((item, index) => (
            <MobileServiceCard
              key={`m-flight-${index}`}
              label="항공"
              item={item}
              editableLabel={index >= fixedFlightCount}
              canRemove={index >= fixedFlightCount}
              onChange={(key, value) => updateService('flight', index, key, value)}
              onRemove={() => removeFlightRow(index)}
            />
          ))}
          <MobileServiceCard label="렌트" item={serviceRows.car[0]} onChange={(key, value) => updateService('car', 0, key, value)} />
          {serviceRows.hotel.map((item, index) => (
            <MobileServiceCard
              key={`m-hotel-${index}`}
              label="호텔"
              item={item}
              editableLabel={index >= fixedHotelCount}
              canRemove={index >= fixedHotelCount}
              onChange={(key, value) => updateService('hotel', index, key, value)}
              onRemove={() => removeHotelRow(index)}
            />
          ))}
        </div>
      </MobileSection>

      <MobileSection title="결제내역" sub="추가 결제가 있으면 카드입금/현금영수증 확인까지 같이 남깁니다." actions={<button type="button" style={mobileMiniBtn} onClick={addPayment}>+ 결제</button>}>
        <div style={mobileCardList}>
          {payments.length === 0 ? <div style={mobileEmpty}>추가 결제내역 없음</div> : payments.map((payment, index) => (
            <MobilePaymentCard key={payment.id || index} payment={payment} index={index} onChange={updatePayment} onRemove={removePayment} />
          ))}
        </div>
      </MobileSection>

      <MobileSection title="환불내역" sub="현금 환불은 현금영수증 취소승인번호를 같이 확인합니다." actions={<button type="button" style={mobileMiniBtn} onClick={addRefund}>+ 환불</button>}>
        <div style={mobileCardList}>
          {refunds.length === 0 ? <div style={mobileEmpty}>환불내역 없음</div> : refunds.map((refund, index) => (
            <MobileRefundCard key={refund.id || index} refund={refund} index={index} onChange={updateRefund} onRemove={removeRefund} />
          ))}
        </div>
      </MobileSection>
    </div>
  );
}

function MobileSection({ title, sub, actions, children }) {
  return (
    <section style={mobileSection}>
      <div style={mobileSectionHead}>
        <div>
          <div style={mobileSectionTitle}>{title}</div>
          {sub && <div style={mobileSectionSub}>{sub}</div>}
        </div>
        {actions && <div style={mobileActions}>{actions}</div>}
      </div>
      {children}
    </section>
  );
}

function Metric({ label, value, danger }) {
  return (
    <div style={mobileMetric}>
      <span style={mobileMetricLabel}>{label}</span>
      <strong style={mobileMetricValue(danger || num(value) < 0)}>{fmt(value)}</strong>
    </div>
  );
}

function MobileServiceCard({ label, item = {}, editableLabel, canRemove, onChange, onRemove }) {
  const effective = effectiveServiceItem(item);
  const fee = cancelFee(effective);
  const effect = supplierEffect(effective);
  return (
    <div style={mobileCard}>
      <div style={mobileCardHead}>
        <div style={mobileCardTitle}>
          <span style={mobileCardName}>{item.item_label || label}</span>
          <span style={mobileCardSub}>{label} 반영 {fmt(effect)}</span>
        </div>
        <strong style={mobileEffect(num(effect) < 0)}>{fmt(effect)}</strong>
      </div>
      {editableLabel && (
        <input value={item.item_label || ''} onChange={(e) => onChange('item_label', e.target.value)} placeholder={`${label} 구분`} style={mobileLabelInput} />
      )}
      {item.item_type === 'flight' ? <MobileFlightPartsReadOnly item={item} /> : <ReadOnlyLine label="기준 원가" value={item.target_cost} />}
      {item.item_type === 'flight' ? (
        <>
          <MobileFlightPartsInput title="환불액" item={item} group="refund" onChange={onChange} />
          <ReadOnlyLine label="취소수수료 자동" value={fee} danger={fee < 0} />
          <MobileFlightPartsInput title="추가/재예약 원가" item={item} group="additional" onChange={onChange} />
        </>
      ) : (
        <>
          <MobileAmountField label="환불액" value={item.supplier_refund} onChange={(value) => onChange('supplier_refund', value)} />
          <ReadOnlyLine label="취소수수료 자동" value={fee} danger={fee < 0} />
          <MobileAmountField label="추가/재예약 원가" value={item.additional_cost} onChange={(value) => onChange('additional_cost', value)} />
        </>
      )}
      {canRemove && <button type="button" style={mobileDangerBtn} onClick={onRemove}>삭제</button>}
    </div>
  );
}

function MobileFlightPartsReadOnly({ item }) {
  return (
    <div style={mobilePartBox}>
      <div style={mobilePartTitle}>기준 원가</div>
      <div style={mobilePartGrid}>
        {FLIGHT_PART_GROUPS.target.map((key, index) => (
          <ReadOnlyLine key={key} compact label={PART_LABELS[index]} value={item[key]} />
        ))}
      </div>
      <div style={mobilePartTotal}>합계 {fmt(flightPartTotal(item, 'target') || num(item.target_cost))}</div>
    </div>
  );
}

function MobileFlightPartsInput({ title, item, group, onChange }) {
  return (
    <div style={mobilePartBox}>
      <div style={mobilePartTitle}>{title}</div>
      <div style={mobilePartGrid}>
        {FLIGHT_PART_GROUPS[group].map((key, index) => (
          <MobileAmountField key={key} label={PART_LABELS[index]} value={item[key]} onChange={(value) => onChange(key, value)} />
        ))}
      </div>
      <div style={mobilePartTotal}>합계 {fmt(flightPartTotal(item, group))}</div>
    </div>
  );
}

function ReadOnlyLine({ label, value, danger, compact }) {
  return (
    <div style={compact ? { display: 'grid', gap: 3, minWidth: 0 } : mobileReadonlyLine}>
      <span style={mobileReadonlyLabel}>{label}</span>
      <strong style={mobileReadonlyValue(danger || num(value) < 0)}>{fmt(value)}</strong>
    </div>
  );
}

function MobileAmountField({ label, value, onChange }) {
  return (
    <label style={mobileFieldWrap}>
      <span style={mobileFieldLabel}>{label}</span>
      <input type="text" inputMode="text" pattern="-?[0-9,]*" value={formatAmountInput(value)} onFocus={(e) => e.target.select()} onChange={(e) => onChange(parseAmountInput(e.target.value))} style={mobileAmountInput} />
    </label>
  );
}
